import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";

interface Permission {
  id: string;
  name: string;
  module: string;
  action: string;
  slug: string;
}

interface PermissionsResult {
  permissions: Permission[];
  roleSlug: string | null;
}

export function usePermissions() {
  const { isAdminSaas } = useTenant();

  const { data, isLoading } = useQuery<PermissionsResult>({
    queryKey: ["user-permissions"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { permissions: [], roleSlug: null };
      }

      if (isAdminSaas) {
        const { data: allPermissions, error } = await supabase
          .from("permissions" as any)
          .select("*") as any;

        if (error) throw error;
        return {
          permissions: (allPermissions as Permission[]) ?? [],
          roleSlug: "admin_saas",
        };
      }

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select(`
          role_id,
          roles:role_id (
            slug
          )
        `)
        .eq("id", user.id)
        .maybeSingle() as any;

      if (profileError) throw profileError;

      const roleSlug: string | null = profile?.roles?.slug ?? null;

      if (!profile?.role_id) {
        return { permissions: [], roleSlug };
      }

      // Para admins recém-criados, garanta acesso completo mesmo sem seeds
      if (roleSlug === "admin") {
        const { data: allPermissions, error } = await supabase
          .from("permissions" as any)
          .select("*") as any;

        if (error) throw error;
        return {
          permissions: (allPermissions as Permission[]) ?? [],
          roleSlug,
        };
      }

      const { data: rolePermissions, error: permError } = await supabase
        .from("role_permissions_junction" as any)
        .select(`
          permission_id,
          permissions:permission_id (
            id,
            name,
            module,
            action,
            slug
          )
        `)
        .eq("role_id", profile.role_id) as any;

      if (permError) throw permError;

      return {
        permissions: (rolePermissions || [])
          .map((rp: any) => rp.permissions)
          .filter(Boolean) as Permission[],
        roleSlug,
      };
    },
  });

  const userPermissions = data?.permissions ?? [];
  const roleSlug = data?.roleSlug ?? null;
  const isAdminTenant = roleSlug === "admin";

  const hasPermission = (module: string, action: string): boolean => {
    if (isAdminSaas || isAdminTenant) return true;
    return userPermissions.some((p) => p.module === module && p.action === action);
  };

  const hasAnyPermission = (permissions: Array<{ module: string; action: string }>): boolean => {
    if (isAdminSaas || isAdminTenant) return true;
    return permissions.some(({ module, action }) => hasPermission(module, action));
  };

  const hasAllPermissions = (permissions: Array<{ module: string; action: string }>): boolean => {
    if (isAdminSaas || isAdminTenant) return true;
    return permissions.every(({ module, action }) => hasPermission(module, action));
  };

  const canAccessModule = (module: string): boolean => {
    if (isAdminSaas || isAdminTenant) return true;
    return userPermissions.some((p) => p.module === module);
  };

  return {
    permissions: userPermissions,
    isLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessModule,
    isAdminSaas,
    isAdminTenant,
  };
}
